import type { Rect, UiRects } from "../../game/input";
import type { InboxMessage } from "../../social/messageService";
import type { RenderModel } from "../renderModel";
import { GAME_FONT, roundedRectPath, type PhoneMetrics } from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface PhoneMessagesDeps {
  drawPhoneMessageDetail: (
    ctx: CanvasRenderingContext2D, model: RenderModel,
    msg: InboxMessage,
    scrX: number, scrY: number, scrW: number, scrH: number,
    contentX: number, contentW: number, contentRight: number,
    metrics: PhoneMetrics, uiRects: UiRects,
  ) => void;
  drawNotificationPanel: (
    rect: Rect,
    options: { fill: string; stroke?: string; accent?: string; radius?: number; lineWidth?: number },
  ) => void;
  staggerAlpha: (index: number, delayMs: number, durationMs: number) => number;
  wrapTextToWidth: (text: string, maxWidth: number, maxLines: number) => string[];
  trimTextToWidth: (text: string, maxWidth: number) => string;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawPhoneMessagesScreen(
  deps: PhoneMessagesDeps,
  ctx: CanvasRenderingContext2D,
  model: RenderModel,
  uiRects: UiRects,
  scrX: number, scrY: number, scrW: number, scrH: number,
  contentX: number, contentW: number, contentRight: number, px: number,
  metrics: PhoneMetrics,
): void {
  // If a message is open, show the detail view instead
  if (model.phoneMessageId != null) {
    const msg = model.inboxMessages.find(m => m.id === model.phoneMessageId);
    if (msg) {
      deps.drawPhoneMessageDetail(ctx, model, msg, scrX, scrY, scrW, scrH, contentX, contentW, contentRight, metrics, uiRects);
      return;
    }
  }

  const desktop = metrics.desktop;
  let curY = scrY + metrics.topPad;
  ctx.fillStyle = "#64d8ff";
  ctx.font = `bold ${metrics.titleFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("INBOX", scrX + scrW / 2, curY);

  curY += metrics.titleGap;

  // Divider
  ctx.fillStyle = "#2a3a55";
  for (let dx = contentX; dx < contentRight; dx += 4) {
    ctx.fillRect(dx, curY, 2, px);
  }
  curY += metrics.sectionGap;

  // Render inbox messages (unified: friend requests + system messages)
  const inbox = model.inboxMessages;
  const maxRows = 6;
  for (let i = 0; i < Math.min(inbox.length, maxRows); i++) {
    const msg = inbox[i];
    const cardH = metrics.messageCardH;
    if (curY + cardH > scrY + scrH - 4) break;

    const alpha = deps.staggerAlpha(i, 60, 200);
    if (alpha <= 0) { curY += cardH + metrics.rowGap; continue; }
    if (alpha < 1) { ctx.save(); ctx.globalAlpha = alpha; }

    // Card background
    if (desktop) {
      deps.drawNotificationPanel(
        { x: contentX, y: curY, w: contentW, h: cardH },
        { fill: "rgba(16,29,49,0.95)", stroke: "rgba(92,140,212,0.26)", accent: msg.accentColor, radius: metrics.cardRadius }
      );
    } else {
      ctx.fillStyle = "#162030";
      ctx.fillRect(contentX, curY, contentW, cardH);
      ctx.fillStyle = msg.accentColor;
      ctx.fillRect(contentX, curY, 3, cardH);
    }

    // Avatar placeholder (colored square with first initial)
    const avatarSize = desktop ? 18 : 14;
    const avatarX = contentX + 8;
    const avatarY = curY + (desktop ? 8 : 6);
    ctx.fillStyle = msg.accentColor;
    ctx.fillRect(avatarX, avatarY, avatarSize, avatarSize);
    ctx.fillStyle = "#0c1624";
    ctx.font = `bold ${desktop ? 9 : 7}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(msg.senderName.charAt(0), avatarX + avatarSize / 2, avatarY + avatarSize / 2);

    // Sender name
    const textLeft = contentX + 8 + avatarSize + 6;
    ctx.fillStyle = msg.accentColor;
    ctx.font = `bold ${metrics.metaFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText(msg.senderName, textLeft, curY + (desktop ? 7 : 4));

    // Body preview
    ctx.fillStyle = "#8a9ab0";
    ctx.font = `${metrics.metaFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    const maxTW = contentW - (desktop ? 56 : 34);
    const textLines = desktop
      ? deps.wrapTextToWidth(msg.body, maxTW, 2)
      : [deps.trimTextToWidth(msg.body, maxTW)];
    const bodyStartY = curY + (desktop ? 18 : 16);
    const lineGap = desktop ? 8 : 0;
    textLines.forEach((line, lineIndex) => {
      ctx.fillText(line, textLeft, bodyStartY + lineIndex * lineGap);
    });

    // Time (local HH:MM) underneath message
    const msgDate = new Date(msg.createdAt);
    const timeStr = `${String(msgDate.getHours()).padStart(2, "0")}:${String(msgDate.getMinutes()).padStart(2, "0")}`;
    ctx.fillStyle = "#5a6a80";
    ctx.font = `${desktop ? metrics.helperFontPx : metrics.helperFontPx - 1}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    const timeY = bodyStartY + textLines.length * (lineGap || 8) + 3;
    ctx.fillText(timeStr, textLeft, timeY);

    // Unread dot
    if (!msg.read) {
      ctx.fillStyle = msg.accentColor;
      if (desktop) {
        roundedRectPath(ctx, contentRight - 14, curY + cardH / 2 - 5, 8, 8, 3);
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.arc(contentRight - 6, curY + cardH / 2 + 4, 3, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Set tap rect
    const rowKey = `inboxRow${i}` as keyof UiRects;
    (uiRects as Record<string, Rect>)[rowKey] = { x: contentX, y: curY, w: contentW, h: cardH };

    if (alpha < 1) ctx.restore();
    curY += cardH + metrics.rowGap;
  }

  // Empty state
  if (inbox.length === 0) {
    ctx.fillStyle = "#5a7a9a";
    ctx.font = `${metrics.metaFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("NO MESSAGES", scrX + scrW / 2, curY + 20);
  }
}
